import { useEffect, useState } from 'react';
import { merge } from 'lodash-es';
import Router from 'next/router';
import { proxy } from 'valtio';
import { isUUID4 } from '@soundxyz/utils';
import {
  ERROR_TYPE,
  PILLARS,
  UPLOAD_RELEASE_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { useAuthArtist } from '@hooks/useAuthArtist';
import { useLogError } from '@hooks/useLogErrorNew';
import { PersistenceStorage } from '@store/persistence';
import { NAV_LINKS } from '@utils/constants';
import { uploadToolInitialState } from './initial-states';
import { UploadToolPersistSchema, UploadToolSchemaKey } from './schema';
import type { UploadToolState } from './schema';

export const uploadToolState: UploadToolState = proxy(uploadToolInitialState());

export function getUploadToolPersistence({ id }: { id: string }) {
  return PersistenceStorage({
    schema: UploadToolPersistSchema,
    key: UploadToolSchemaKey(id),
    eager: true,
  });
}

/*
 * This Hook is meant to be used JUST ONCE in '/dashboard/upload/[draftId]/[stepId]'
 * It loads the local storage draft into a valtio store
 * As we only update local storage on "Save and Continue", using this will reset valtio store.
 */
export function useUploadTool({ id }: { id: string | null }) {
  const { authArtist } = useAuthArtist();

  const [loading, setLoading] = useState(true);
  const logError = useLogError();

  useEffect(() => {
    if (!id) return;

    async function getPersisted() {
      if (!id || !authArtist?.id) return;

      try {
        setLoading(true);

        const persisted = await getUploadToolPersistence({ id }).get();

        // There is no draft in local storage, throw warning
        if (!persisted) {
          const errorMessage = `Draft with id ${id} does not exist.`;
          logError({
            action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_UPLOAD_CREATE_DRAFT_FAILED,
            error: errorMessage,
            errorType: ERROR_TYPE.LOCAL_STORAGE,
            message: errorMessage,
            level: 'warning',
            isArtistOnlyPage: true,
            pillar: PILLARS.UPLOAD_RELEASE,
          });

          Router.push(NAV_LINKS.Releases);

          return;
        }

        // If auth artist id do not match local storage ID, throw warning and redirect
        if (persisted.artistId !== authArtist.id) {
          const errorMessage = `Draft does not match artist.`;
          logError({
            action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_UPLOAD_CREATE_DRAFT_FAILED,
            error: errorMessage,
            errorType: ERROR_TYPE.LOCAL_STORAGE,
            message: errorMessage,
            level: 'warning',
            isArtistOnlyPage: true,
            pillar: PILLARS.UPLOAD_RELEASE,
          });

          Router.replace(NAV_LINKS.Releases);

          return;
        }

        Object.assign(uploadToolState, merge(uploadToolState, persisted));

        setLoading(false);
      } catch (error) {
        const errorMessage = "Couldn't get/populate draft from local storage";
        logError({
          action: UPLOAD_RELEASE_ERROR_ACTIONS.RELEASE_UPLOAD_CREATE_DRAFT_FAILED,
          error: error,
          errorType: ERROR_TYPE.LOCAL_STORAGE,
          message: errorMessage,
          level: 'fatal',
          isArtistOnlyPage: true,
          pillar: PILLARS.UPLOAD_RELEASE,
        });
      }
    }

    if (isUUID4(id) && authArtist?.id) {
      getPersisted();
    }
  }, [id, logError, authArtist]);

  return { loading, state: uploadToolState };
}
